import { useRovingTabs } from "@/hooks/useRovingTabs.hook";
import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
} from "react";
import type { CampaignChannel } from "../../../data";

type ChannelFilter = CampaignChannel | "all";

const subscribeToHash = (onChange: () => void) => {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
};
const readHash = () => window.location.hash.slice(1);
const readServerHash = () => "";

// The campaign studio: one category tab and one channel filter. A category
// named in the URL hash (`#winback`) is shown until a tab is chosen, and
// clicking any in-page link to a category (the Explore cards) opens it.
export function useCampaignStudio(keys: string[]) {
  const hash = useSyncExternalStore(subscribeToHash, readHash, readServerHash);
  const tabs = useRovingTabs(keys.length);
  const selectIndex = tabs.select;

  const initialState = { channel: "all" as ChannelFilter, hasChosen: false };
  const [state, setState] = useState(initialState);

  // Picking a category (click, keyboard or in-page link) overrides the hash.
  const select = useCallback(
    (key: string) => {
      const index = keys.indexOf(key);
      if (index < 0) return;
      selectIndex(index);
      setState((state) => ({ ...state, hasChosen: true }));
    },
    [keys, selectIndex],
  );
  const onTabKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    tabs.onTabKeyDown(event, index);
    setState((state) => (state.hasChosen ? state : { ...state, hasChosen: true }));
  };
  const setChannel = (channel: ChannelFilter) =>
    setState((state) => ({ ...state, channel }));

  useEffect(() => {
    // Same-page hash links go through history.pushState, which fires no
    // hashchange, so listen for the clicks themselves.
    function openLinkedCategory(event: MouseEvent) {
      const link = (event.target as Element | null)?.closest?.("a[href]");
      const target = link?.getAttribute("href")?.split("#")[1];
      if (target && keys.includes(target)) select(target);
    }
    document.addEventListener("click", openLinkedCategory);
    return () => document.removeEventListener("click", openLinkedCategory);
  }, [keys, select]);

  return {
    active:
      !state.hasChosen && keys.includes(hash) ? hash : keys[tabs.activeIndex],
    select,
    onTabKeyDown,
    channel: state.channel,
    setChannel,
  };
}
